"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useState } from "react"
import { Form, Formik, useFormikContext } from "formik"
import { useQuery } from "@tanstack/react-query"

import { FormInput, FormSelect, FormTextarea } from "@/components/form-inputs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { useSaveMetaAdsFormFollowUpActionsMutation } from "@/hooks/mutations/meta-ads"
import { useMetaAdsFollowUpFormQuery, useMetaAdsFollowUpOptionsQuery, useMetaAdsSetupOptionsQuery } from "@/hooks/queries/meta-ads"
import type { MetaAdsIntegrationView, MetaFollowUpAction, MetaFormFollowUpActionsInput } from "@/lib/meta-ads/types"
import { getMetaAdsFollowUpTemplatesAction } from "@/server-actions/meta-ads"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type FormValues = {
  primaryActionType: MetaFollowUpAction["actionType"] | "none"
  fallbackActionType: MetaFollowUpAction["actionType"] | "none"
  fallbackDelay: string
  voiceAgentId: string
  callerPhoneNumber: string
  voicePhoneFieldKey: string
  voiceInstructions: string
  voiceDelay: string
  voiceWorkingHours: boolean
  emailBinding: string
  emailFieldKey: string
  emailSubject: string
  emailBusinessName: string
  emailInstructions: string
  emailDelay: string
  emailWorkingHours: boolean
  messageBinding: string
  messagePhoneFieldKey: string
  messageTemplate: string
  messageParameterFields: string
  messageBusinessName: string
  messageInstructions: string
  messageDelay: string
  messageWorkingHours: boolean
}

export function MetaAdsFollowUpDialog({ open, onOpenChange, integration, initialPrimaryActionType, onSaved }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: MetaAdsIntegrationView
  initialPrimaryActionType?: MetaFollowUpAction["actionType"]
  onSaved?: (integration: MetaAdsIntegrationView) => void
}) {

  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsFollowUpDialog")
  const tCommonUi = useTranslations("Common")

  const options = useMetaAdsFollowUpOptionsQuery(open)
  const setup = useMetaAdsSetupOptionsQuery(open)
  const save = useSaveMetaAdsFormFollowUpActionsMutation()
  const [formId, setFormId] = useState("")
  const selectedFormId = formId || options.data?.forms[0]?.id || ""
  const form = useMetaAdsFollowUpFormQuery(selectedFormId, open)
  const initialValues = createValues(integration, selectedFormId, initialPrimaryActionType)
  const fields = form.data?.fields.map(field => ({ value: field.key, label: `${field.label} (${field.key})` })) ?? []

  async function submit(values: FormValues): Promise<void> {
    if (!selectedFormId || !form.data) throw new Error("Choose an Instant Form.")
    const actions = buildActions(values, form.data.disclosureText)
    const updated = await save.mutateAsync({ formId: selectedFormId, actions })
    onSaved?.(updated)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle><span key={tUi("chooseTheFirstTaskAndFallback")}>{tUi("chooseTheFirstTaskAndFallback")}</span></DialogTitle>
          <DialogDescription><span key={tUi("setOneFirstTaskForEachInstantForm")}>{tUi("setOneFirstTaskForEachInstantForm")}</span></DialogDescription>
        </DialogHeader>
        {options.isLoading ? <p><span key={tUi("loadingFormsAndAvailableAgents")}>{tUi("loadingFormsAndAvailableAgents")}</span></p> : null}
        {options.error ? <Alert variant="destructive"><AlertDescription><UiErrorText message={options.error.message} /></AlertDescription></Alert> : null}
        {options.data?.forms.length === 0 ? <p className="text-sm text-muted-foreground"><span key={tUi("noActiveInstantFormsAreInThisConnection")}>{tUi("noActiveInstantFormsAreInThisConnection")}</span></p> : null}
        {options.data && selectedFormId ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="meta-follow-up-form"><span key={tUi("instantForm")}>{tUi("instantForm")}</span></Label>
              <select id="meta-follow-up-form" className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={selectedFormId}
                onChange={event => setFormId(event.target.value)}>
                {options.data.forms.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </div>
            {form.isLoading ? <p><span key={tUi("loadingFormFieldsAndContactDisclosure")}>{tUi("loadingFormFieldsAndContactDisclosure")}</span></p> : null}
            {form.error ? <Alert variant="destructive"><AlertDescription><UiErrorText message={form.error.message} /></AlertDescription></Alert> : null}
            {form.data ? (
              <Formik key={selectedFormId} initialValues={initialValues} enableReinitialize onSubmit={values => submit(values).catch(() => {})}>
                {({ values, setFieldValue, isSubmitting }) => (
                  <Form className="space-y-5">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="meta-primary-action"><span key={tUi("firstTaskForEachLead")}>{tUi("firstTaskForEachLead")}</span></Label>
                        <select id="meta-primary-action" className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                          value={values.primaryActionType} onChange={event => {
                            void setFieldValue("primaryActionType", event.target.value)
                            if (event.target.value === "none") void setFieldValue("fallbackActionType", "none")
                          }}>
                          <option key={tUi("doNotFollowUp")} value="none">{tUi("doNotFollowUp")}</option>
                          <option key={tUi("scheduleCall")} value="schedule_call">{tUi("scheduleCall")}</option>
                          <option key={tUi("sendEmail")} value="send_email" disabled={!form.data.disclosureText || options.data.emailBindings.length === 0}>{tUi("sendEmail")}</option>
                          <option key={tUi("sendWhatsApp")} value="send_whatsapp" disabled={!form.data.disclosureText || options.data.whatsappChannels.length === 0}>{tUi("sendWhatsApp")}</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="meta-fallback-action"><span key={tUi("fallbackIfTheFirstTaskFails")}>{tUi("fallbackIfTheFirstTaskFails")}</span></Label>
                        <select id="meta-fallback-action" className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                          value={values.fallbackActionType} disabled={values.primaryActionType === "none"}
                          onChange={event => void setFieldValue("fallbackActionType", event.target.value)}>
                          <option key={tUi("noFallback")} value="none">{tUi("noFallback")}</option>
                          <option key={tUi("scheduleCall")} value="schedule_call">{tUi("scheduleCall")}</option>
                          <option key={tUi("sendEmail")} value="send_email" disabled={!form.data.disclosureText || options.data.emailBindings.length === 0}>{tUi("sendEmail")}</option>
                          <option key={tUi("sendWhatsApp")} value="send_whatsapp" disabled={!form.data.disclosureText || options.data.whatsappChannels.length === 0}>{tUi("sendWhatsApp")}</option>
                        </select>
                      </div>
                    </div>
                    {values.fallbackActionType !== "none" && values.fallbackActionType === values.primaryActionType ? (
                      <FormInput name="fallbackDelay" label={tUi("waitBeforeRepeatingMinutes")} type="number" min={1} max={10080}
                        description={tUi("theFallbackIsScheduledOnlyAfterTheFirst")} />
                    ) : null}
                    {values.primaryActionType === "schedule_call" || values.fallbackActionType === "schedule_call" ? <section className="space-y-3 rounded-lg border p-4">
                      <h3 className="font-semibold"><span key={tUi("scheduleCall")}>{tUi("scheduleCall")}</span></h3>
                        <FormSelect name="voiceAgentId" label={tUi("voiceAgent")} options={setup.data?.voiceAgents.map(item => ({ value: item.id, label: item.name })) ?? []} emptyMessage={tUi("noActiveVoiceAgents")} />
                        <FormSelect name="callerPhoneNumber" label={tUi("callerNumber")} options={setup.data?.phoneNumbers.map(item => ({ value: item.phoneNumber, label: item.label ?? item.phoneNumber })) ?? []} emptyMessage={tUi("noOutboundNumbers")} />
                        <FormSelect name="voicePhoneFieldKey" label={tUi("leadPhoneField")} options={fields} emptyMessage={tUi("noFormFields")} />
                        <FormTextarea name="voiceInstructions" label={tUi("callbackInstructions")} maxLength={2000} />
                        <ActionTiming prefix="voice" />
                    </section> : null}
                    {values.primaryActionType === "send_email" || values.fallbackActionType === "send_email" ? <section className="space-y-3 rounded-lg border p-4">
                      <h3 className="font-semibold"><span key={tUi("sendEmail")}>{tUi("sendEmail")}</span></h3>
                        <FormSelect name="emailBinding" label={tUi("emailAgentAndSendingMailbox")} options={options.data.emailBindings.map(item => ({ value: `${item.agent_id}:${item.inbox_id}`, label: `${item.agent_name} · ${item.inbox_address}` }))} emptyMessage={tUi("noActiveEmailAgentsPermittedToSendSet")} />
                        <FormSelect name="emailFieldKey" label={tUi("leadEmailField")} options={fields} emptyMessage={tUi("noFormFields")} />
                        <FormInput name="emailSubject" label={tUi("emailSubject")} maxLength={255} />
                        <FormInput name="emailBusinessName" label={tUi("sendingBusinessName")} maxLength={255} />
                        <FormTextarea name="emailInstructions" label={tUi("emailInstructions")} maxLength={2000} />
                        <ActionTiming prefix="email" />
                    </section> : null}
                    {values.primaryActionType === "send_whatsapp" || values.fallbackActionType === "send_whatsapp" ? <section className="space-y-3 rounded-lg border p-4">
                      <h3 className="font-semibold"><span key={tUi("sendWhatsApp")}>{tUi("sendWhatsApp")}</span></h3>
                        <FormSelect name="messageBinding" label={tUi("messagingAgentAndWhatsAppChannel")} options={options.data.whatsappChannels.map(item => ({ value: `${item.agent_id}:${item.channel_id}`, label: `${item.agent_name} · ${item.channel_name ?? item.channel_id}` }))} emptyMessage={tUi("noActiveMessagingAgentsWithConnectedWhatsAppChannels")} onValueChange={() => void setFieldValue("messageTemplate", "")} />
                        <FormSelect name="messagePhoneFieldKey" label={tUi("leadPhoneField")} options={fields} emptyMessage={tUi("noFormFields")} />
                        <TemplateField channelId={values.messageBinding.split(":")[1] ?? ""} />
                        <FormInput name="messageParameterFields" label={tUi("templateVariableFieldKeys")} description={tUi("enterFormFieldKeysInTemplateOrderSeparated")} />
                        <FormInput name="messageBusinessName" label={tUi("sendingBusinessName")} maxLength={255} />
                        <FormTextarea name="messageInstructions" label={tUi("messageInstructions")} maxLength={2000} />
                        <ActionTiming prefix="message" />
                    </section> : null}
                    {!form.data.disclosureText ? <p className="text-sm text-muted-foreground"><span key={tUi("addAContactDisclosureToThisFormIn")}>{tUi("addAContactDisclosureToThisFormIn")}</span></p> : null}
                    {options.data.emailBindings.length === 0 ? <p className="text-sm text-muted-foreground"><span key={tUi("connectASendingMailboxAndSetAnActive")}>{tUi("connectASendingMailboxAndSetAnActive")}</span></p> : null}
                    {options.data.whatsappChannels.length === 0 ? <p className="text-sm text-muted-foreground"><span key={tUi("connectAWhatsAppChannelToAnActiveMessaging")}>{tUi("connectAWhatsAppChannelToAnActiveMessaging")}</span></p> : null}
                    <Alert><AlertDescription>
                      <span key={tUi("theFormMustClearlySayThatTheNamed")}>{tUi("theFormMustClearlySayThatTheNamed")}</span></AlertDescription></Alert>
                    <p className="text-xs text-muted-foreground"><TranslationText>{tUi.rich("currentFormDisclosureValue0", { value0: String(form.data.disclosureText || "No custom contact disclosure found.") })}</TranslationText></p>
                    {save.error ? <p role="alert" className="text-sm text-destructive"><UiErrorText message={save.error.message} /></p> : null}
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
                      <Button type="submit" disabled={isSubmitting || save.isPending}><span key={tUi("saveFollowUp")}>{tUi("saveFollowUp")}</span></Button>
                    </DialogFooter>
                  </Form>
                )}
              </Formik>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function ToggleField({ id, label, checked, disabled, onChange }: {
  id: string; label: string; checked: boolean; disabled?: boolean; onChange: (value: boolean) => void
}) {
  return <div className="flex items-center gap-2"><Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={value => onChange(value === true)} /><Label htmlFor={id} className="font-semibold">{label}</Label></div>
}

function ActionTiming({ prefix }: { prefix: "voice" | "email" | "message" }) {
  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsFollowUpDialog")

  return <div className="grid gap-3 sm:grid-cols-2">
    <FormInput name={`${prefix}Delay`} label={tUi("delayMinutes")} type="number" min={0} max={10080} />
    <div className="flex items-center gap-2 pt-4"><FormWorkingHours prefix={prefix} /></div>
  </div>
}

function FormWorkingHours({ prefix }: { prefix: "voice" | "email" | "message" }) {
  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsFollowUpDialog")

  const { values, setFieldValue } = useFormikContext<FormValues>()
  const name = `${prefix}WorkingHours` as const
  return <ToggleField id={`meta-${prefix}-hours`} label={tUi("keepInsideWorkingHours")} checked={values[name]} onChange={checked => void setFieldValue(name, checked)} />
}

function TemplateField({ channelId }: { channelId: string }) {
  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsFollowUpDialog")

  const query = useQuery({
    queryKey: ["meta-ads-whatsapp-templates", channelId], enabled: Boolean(channelId),
    queryFn: async () => {
      const result = await getMetaAdsFollowUpTemplatesAction({ channelId })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
  return <FormSelect name="messageTemplate" label={tUi("approvedWhatsAppTemplate")} options={query.data?.templates.map(template => ({ value: `${template.name}:${template.language}`, label: `${template.name} (${template.language}) · ${template.parameter_count} variables` })) ?? []} emptyMessage={tUi("noApprovedTemplatesCreateAndApproveOneIn")} description={query.error?.message} />
}

function createValues(
  integration: MetaAdsIntegrationView,
  formId: string,
  initialPrimaryActionType?: MetaFollowUpAction["actionType"],
): FormValues {
  const actions = integration.form_action_overrides[formId]
  const voice = actions?.find(action => action.actionType === "schedule_call")
  const email = actions?.find(action => action.actionType === "send_email")
  const message = actions?.find(action => action.actionType === "send_whatsapp")
  const savedFallback = actions?.[1]
  const repeatDelay = savedFallback?.actionType === actions?.[0]?.actionType ? savedFallback?.delayMinutes : undefined
  return {
    primaryActionType: actions ? actions[0]?.actionType ?? "none" : initialPrimaryActionType ?? (integration.voice_agent_id ? "schedule_call" : "none"),
    fallbackActionType: savedFallback?.actionType ?? "none",
    fallbackDelay: String(repeatDelay ?? 60),
    voiceAgentId: voice?.voiceAgentId ?? integration.voice_agent_id ?? "",
    callerPhoneNumber: voice?.callerPhoneNumber ?? integration.caller_phone_number ?? "",
    voicePhoneFieldKey: voice?.phoneFieldKey ?? integration.phone_field_key,
    voiceInstructions: voice?.instructions ?? "Contact this new Meta Ads lead promptly. Confirm their request and help with their enquiry.",
    voiceDelay: String(voice?.delayMinutes ?? 0), voiceWorkingHours: voice?.respectWorkingHours ?? integration.respect_working_hours,
    emailBinding: email ? `${email.emailAgentId}:${email.inboxId}` : "",
    emailFieldKey: email?.emailFieldKey ?? "email", emailSubject: email?.subject ?? "Following up on your enquiry",
    emailBusinessName: email?.businessName ?? "", emailInstructions: email?.instructions ?? "Reply to the lead's enquiry.",
    emailDelay: String(email?.delayMinutes ?? 0), emailWorkingHours: email?.respectWorkingHours ?? true,
    messageBinding: message ? `${message.messageAgentId}:${message.channelId}` : "",
    messagePhoneFieldKey: message?.phoneFieldKey ?? integration.phone_field_key,
    messageTemplate: message ? `${message.templateName}:${message.templateLanguage}` : "",
    messageParameterFields: message?.templateParameterFieldKeys.join(", ") ?? "",
    messageBusinessName: message?.businessName ?? "", messageInstructions: message?.instructions ?? "Follow up on the lead's enquiry.",
    messageDelay: String(message?.delayMinutes ?? 0), messageWorkingHours: message?.respectWorkingHours ?? true,
  }
}

function buildActions(values: FormValues, disclosureText: string): MetaFormFollowUpActionsInput["actions"] {
  if (values.primaryActionType === "none") return []
  const primary = buildAction(values.primaryActionType, values, disclosureText)
  if (values.fallbackActionType === "none") return [primary]
  const fallback = values.fallbackActionType === values.primaryActionType
    ? { ...primary, delayMinutes: Number(values.fallbackDelay) }
    : buildAction(values.fallbackActionType, values, disclosureText)
  return [primary, fallback]
}

function buildAction(actionType: MetaFollowUpAction["actionType"], values: FormValues, disclosureText: string): MetaFollowUpAction {
  switch (actionType) {
    case "schedule_call":
      return { actionType, voiceAgentId: values.voiceAgentId, callerPhoneNumber: values.callerPhoneNumber,
        phoneFieldKey: values.voicePhoneFieldKey, instructions: values.voiceInstructions,
        delayMinutes: Number(values.voiceDelay), respectWorkingHours: values.voiceWorkingHours }
    case "send_email": {
      const [emailAgentId, inboxId] = values.emailBinding.split(":")
      return { actionType, emailAgentId, inboxId, emailFieldKey: values.emailFieldKey,
        subject: values.emailSubject, businessName: values.emailBusinessName, disclosureText,
        instructions: values.emailInstructions, delayMinutes: Number(values.emailDelay), respectWorkingHours: values.emailWorkingHours }
    }
    case "send_whatsapp": {
      const [messageAgentId, channelId] = values.messageBinding.split(":")
      const [templateName, templateLanguage] = values.messageTemplate.split(":")
      return { actionType, messageAgentId, channelId, phoneFieldKey: values.messagePhoneFieldKey,
        templateName, templateLanguage, templateParameterFieldKeys: values.messageParameterFields.split(",").map(item => item.trim()).filter(Boolean),
        businessName: values.messageBusinessName, disclosureText, instructions: values.messageInstructions,
        delayMinutes: Number(values.messageDelay), respectWorkingHours: values.messageWorkingHours }
    }
  }
}
